import React, { useState, useRef, useEffect } from "react";
import Loader from "react-js-loader";
import { useSelector } from "react-redux";

import { Badge, Button, useDisclosure, Box, Text, useToast } from "@chakra-ui/react";
import { startCase, find, uniqBy, upperFirst } from "lodash";
import moment from "moment";
import { BiRefresh } from "react-icons/bi";
import { FiFilter } from "react-icons/fi";

import TransfiGrid from "@/utils/TransfiGrid";
import { TimezoneFormatter } from "@/utils/formatter";
import { callApiWithToken } from "@/utils/http_common";

import UserFilter from "./UserFilter";

export default function Users() {
  const [loading, setLoading] = useState(false);
  const [countriesList, setCountriesList] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [inputData, setInputData] = useState({});
  const [formValues, setFormValues] = useState({});
  const defaultDate = {
    startDate: moment(new Date()).subtract(3, "months").startOf("day"),
    endDate: moment(new Date()).endOf("day"),
  };
  const [filters, setFilters] = useState({ dateRange: defaultDate });
  const [dateRange, setDateRange] = useState(defaultDate);

  const selectedOrg = useSelector((state) => state?.login?.orgDetails?.orgId);
  const gridRef = useRef();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const toast = useToast();

  useEffect(() => {
    getCountries();
  }, []);

  const colorMap = {
    kyc_initiated: "yellow",
    kyc_success: "green",
    approved: "green",
    user_approved: "green",
    active: "green",
    trial_user: "orange",
    pending: "orange",
    address_pending: "orange",
    sof_pending: "orange",
    kyc_pending: "orange",
    kyc_manual_review: "orange",
    kyc_pep_review: "orange",
    kyc_blocked: "red",
    kyc_expired: "red",
    kyc_rejected: "red",
    kyc_failed: "red",
    blocked: "red",
    inactive: "red",
    rejected: "red",
    user_rejected: "red",
    not_applicable: "cyan",
  };

  const getCountries = async () => {
    try {
      setLoading(true);
      const response = await callApiWithToken.get("/common/getCountries");
      const countries = response?.data?.data?.countries || [];
      setCountriesList(countries);
      const list = countries.map((country) => {
        return { value: country.twoDigitCode, label: country.name };
      });
      const uniqueCountries = uniqBy(list, (obj) => obj.value);
      setCountryOptions(uniqueCountries);
    } catch (err) {
      toast({
        title: "Error",
        description: "Unable to fetch countries.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  const refreshGrid = () => {
    gridRef.current.refreshGrid();
  };

  const columns = [
    {
      Header: "Owner",
      accessor: "customer.email",
      show: selectedOrg === "OG-220923101501950" ? true : false,
      width: "150",
      color: "warning",
      Cell: ({ value }) => {
        return value || "NA";
      },
    },
    {
      Header: "Created At",
      accessor: "createdAt",
      width: "200",
      color: "warning",
      Cell: ({ value }) => {
        return TimezoneFormatter(value, "lll", "America/Detroit");
      },
    },
    {
      Header: "USER ID",
      accessor: "userId",
      width: "210",
      color: "warning",
      Cell: ({ value }) => {
        return <Text style={{ color: "#4ACDED", fontWeight: "bold" }}>{value}</Text>;
      },
    },
    {
      Header: "Name",
      accessor: "name",
      width: "200",
      color: "warning",
      Cell: ({ row }) => {
        const { name, firstName, lastName } = row?.original;
        return upperFirst(name) || (firstName && upperFirst(firstName + " " + lastName)) || "NA";
      },
    },
    {
      Header: "Email",
      accessor: "email",
      width: "230",
      color: "warning",
      Cell: ({ value }) => {
        return value || "N/A";
      },
    },
    {
      Header: "Country",
      accessor: "country",
      color: "warning",
      Cell: ({ value }) => {
        const response = find(countriesList, { twoDigitCode: value });
        const countryValue = response ? response?.name + " [" + response?.twoDigitCode + "]" : value;
        return <strong>{countryValue}</strong>;
      },
    },
    {
      Header: "Status",
      accessor: "status",
      color: "warning",
      Cell: ({ value }) => {
        const color = colorMap[value] || "purple";
        return <Badge colorScheme={color}> {startCase(value)} </Badge>;
      },
    },
    {
      Header: "Basic Kyc Status",
      accessor: "basicKycStatus",
      color: "warning",
      Cell: ({ value }) => {
        if (!value) return <Badge colorScheme="cyan"> {"NA"} </Badge>;
        const color = colorMap[value] || "purple";
        return <Badge colorScheme={color}> {startCase(value)} </Badge>;
      },
    },
    {
      Header: "Standard Kyc Status",
      accessor: "standardKycStatus",
      color: "warning",
      Cell: ({ value }) => {
        if (!value) return <Badge colorScheme="cyan"> {"NA"} </Badge>;
        const color = colorMap[value] || "purple";
        return <Badge colorScheme={color}> {startCase(value)} </Badge>;
      },
    },
    {
      Header: "Advance Kyc Status",
      accessor: "advancedKycStatus",
      color: "warning",
      Cell: ({ value }) => {
        if (!value) return <Badge colorScheme="cyan"> {"NA"} </Badge>;
        const color = colorMap[value] || "purple";
        return <Badge colorScheme={color}> {startCase(value)} </Badge>;
      },
    },
  ];

  const handleOnReset = () => {
    setDateRange(defaultDate);
    setFilters({ dateRange: defaultDate });
    setFormValues({});
    setInputData({});
  };

  if (loading) {
    <Loader type="spinner-cub" bgColor={"#FFFFFF"} title={"Loading..."} color={"#FFFFFF"} size={100} />;
  }

  return (
    <Box>
      <div style={{ margin: "2% 0" }}>
        <div style={{ float: "right", marginRight: "5%" }}>
          <Button
            leftIcon={<BiRefresh style={{ fontSize: "20px" }} />}
            onClick={() => refreshGrid()}
            variant={"outline"}
            colorScheme="blue"
            style={{ margin: "0 0px" }}
            size="sm"
          >
            Refresh
          </Button>
          <Button
            leftIcon={<FiFilter style={{ fontSize: "20px" }} />}
            onClick={onOpen}
            variant={"outline"}
            colorScheme="blue"
            style={{ margin: "0 8px" }}
            size="sm"
          >
            Filter
          </Button>
        </div>
      </div>
      <br />
      {/* <CustomTable columns={columnsCustomTable} data={orderData} /> */}
      <TransfiGrid
        ref={gridRef}
        columns={columns}
        dataUrl={"/users/list"}
        filters={{ impersonatedOrg: selectedOrg, filters }}
        collection={"draft_users"}
        database={"transfi-nucleus"}
        gridFilters={{ filters, impersonatedOrg: selectedOrg }}
        headerDetails={{ types: { createdAt: "date" } }}
        gridName={"users"}
      />
      <UserFilter
        isOpen={isOpen}
        onClose={onClose}
        countryOptions={countryOptions}
        inputData={inputData}
        setInputData={setInputData}
        dateRange={dateRange}
        setDateRange={setDateRange}
        filters={filters}
        setFilters={setFilters}
        formValues={formValues}
        setFormValues={setFormValues}
        handleOnReset={handleOnReset}
        orgId={selectedOrg}
      ></UserFilter>
    </Box>
  );
}
